import { type Biome } from '@goodtime/shared';
import { BiomeBackgroundEffects } from './effects/BiomeBackgroundEffects';

/**
 * Vite glob — picks up every PNG in src/assets/map_bg/ and exposes
 * a {biomeId → url} map at runtime. Missing biomes are fine
 * (they fall back to a sky color in the L3 layer).
 */
const BG_MODULES = import.meta.glob<{ default: string }>(
  '../../../assets/map_bg/*.png',
  { eager: true, query: '?url' },
);
const BG_BY_BIOME: Partial<Record<Biome['id'], string>> = Object.fromEntries(
  Object.entries(BG_MODULES)
    .map(([path, mod]) => {
      const id = path.match(/([^/]+)\.png$/)?.[1] ?? null;
      return id ? [id, mod.default] : null;
    })
    .filter((entry): entry is [string, string] => entry !== null),
);

/** Panorama natural aspect — see MapLayerMid for the rationale. */
const PANORAMA_ASPECT = '2172 / 724';

export interface MapLayerBackgroundProps {
  biome: Biome;
  /** Current scroll-left position of the foreground (in px). */
  scrollLeft: number;
  /** Parallax factor — 0..1. 0.25 = scrolls a quarter as fast as the
   * foreground, giving the strongest depth illusion for distant
   * elements. */
  parallax: number;
}

/**
 * Map V2 — Layer 3 (background, slowest).
 *
 * <p>Renders the wide horizontal {@code map_bg/<biomeId>.png} panorama
 * as the back-most layer. Translates with the foreground scroll at a
 * fraction of the speed: when {@code parallax = 0.25}, scrolling the
 * foreground 100px shifts this layer 25px, creating the parallax
 * depth sensation.
 *
 * <p>Sized to fill the viewport height (height: 100%) with width set
 * to {@code auto} so the panorama's natural landscape aspect carries
 * the horizontal extent — wider than the world at any reasonable
 * viewport, so parallax offsets never expose the container fallback.
 *
 * <p>Falls back to the biome's sky-gradient if the panorama hasn't
 * been commissioned yet (biomes other than kingdom in early V2).
 */
export function MapLayerBackground({
  biome,
  scrollLeft,
  parallax,
}: MapLayerBackgroundProps) {
  const bgUrl = BG_BY_BIOME[biome.id];
  const offset = -scrollLeft * parallax;

  if (bgUrl) {
    // Same wrapper pattern as MapLayerMid — the wrapper takes the
    // parallax offset and the image + effect children scale within it.
    // L3 effect children (e.g. aurora) thereby scroll with this layer
    // at 0.25× — anchored to the painted sky, not the viewport.
    return (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
        }}
        aria-hidden
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: offset,
            height: '100%',
            aspectRatio: PANORAMA_ASPECT,
            display: 'block',
          }}
        >
          <img
            src={bgUrl}
            alt=""
            draggable={false}
            // U12 L2 (2026-05-31) — fade to 0 on 404 so the L3 sky
            // gradient + the world-map's outer radial-gradient
            // backdrop carry the scene. Without this, a missing
            // {@code bg.png} renders the browser's broken-image
            // glyph + dimmed alt-text band.
            onError={(e) => { e.currentTarget.style.opacity = '0'; }}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
            }}
          />
          <BiomeBackgroundEffects biomeId={biome.id} />
        </div>
      </div>
    );
  }

  // Fallback — biome sky gradient.
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: `linear-gradient(180deg, ${biome.sky[0]}, ${biome.sky[1]} 60%, ${biome.sky[2]})`,
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden
    />
  );
}
